/* ============================================================
   Guided tours — which tour a user gets, and what each step says.

   A tour shows once per user: the backend keeps the tours a user has finished or
   skipped in users.tours_seen, sent with /users/me, and POST /users/me/tours/{id}
   adds one (backend/users/app.py, migration 130).

   Key: "<role_id>_<version>", value: when — e.g. {"9_version1": "2026-11-02T10:15:00+00:00"}
   (9 = Department Manager). The role id is the user's current role, so the keys also
   tell which role's tour they saw: someone who saw "9_version1" and later moves to
   another role still gets that role's tour. To show a role's tour again, bump its
   `version` (version2): everyone in that role sees it once more.

   Adding a tour for another role: one entry in TOURS (its role key and version).
   The steps are built
   from the user's own RBAC — the same pages their sidebar shows (routes.js +
   can()) — so a role sees exactly its own pages, and an Access Control change
   needs no edit here. The words are plain and short on purpose.
   ============================================================ */

import { NAV_GROUPS, ROUTES } from '../app/routes'

/** 'Department Manager' / 'department_manager' → 'department_manager'. */
export const roleKey = (name) => String(name || '').trim().toLowerCase().replace(/[\s-]+/g, '_')

/** One entry per tour. `roles`: role keys it is for, or ['*'] for every signed-in user.
    `version`: bump it (version2, …) to show the tour once more to everyone it is for. */
export const TOURS = [
  { roles: ['department_manager'], version: 'version1', build: appTour },
]

/** Does this role have a tour? (The "Take the tour" menu item shows only then.) */
export const roleHasTour = (roleName) => {
  const key = roleKey(roleName)
  return Boolean(key) && TOURS.some((t) => t.roles.includes('*') || t.roles.includes(key))
}

/** "Take the tour" (profile menu, phone menu): start this user's tour again, now, even if
    they have seen it. ProductTour listens for this event; nothing is saved by asking. */
export const TOUR_REPLAY_EVENT = 'tf:replay-tour'
export const replayTour = () => window.dispatchEvent(new CustomEvent(TOUR_REPLAY_EVENT))

/** The tours_seen key for this user and tour: "<role_id>_<version>", e.g. "9_version1". */
export const tourKey = (user, tour) => `${user.role_id}_${tour.version}`

/** The tour for this user's role, seen or not, with its `id` (the tours_seen key), or null. */
export function roleTour(user) {
  // No role id, no key to remember it by: no tour.
  if (user?.role_id == null || user.role_id === '') return null
  const key = roleKey(user.role_name)
  const tour = TOURS.find((t) => t.roles.includes('*') || t.roles.includes(key))
  return tour ? { ...tour, id: tourKey(user, tour) } : null
}

/** The tour for this user's role they have not finished or skipped, with its `id`
    (the tours_seen key), or null. */
export function tourFor(user) {
  const seen = user?.tours_seen
  // An older backend sends no tours_seen: never show a tour it cannot remember.
  if (!seen || typeof seen !== 'object') return null
  const tour = roleTour(user)
  if (!tour) return null
  return seen[tour.id] ? null : tour
}

/* ---------------- words ---------------- */

/** What each page is for, in a sentence a child could follow. Every page has one, so a
    tour for any role can use it. */
const PAGE_COPY = {
  dashboard: 'Your home page. A quick look at everything that matters today.',
  inbox: 'New job applications land here, straight from email. Open one to read it.',
  matching: 'We match new applications to the right job for you.',
  import: 'Got a CV as a PDF? Drop it here. We read it for you and save it.',
  candidates: 'Everyone who applied. Tap a name to see their CV, notes and next step.',
  cvbank: 'Good people we have no job for yet. They wait here for the next role.',
  talent: 'Look for new people on LinkedIn who fit a job.',
  tasks: 'Your to-do list.',
  requisitions: 'Need a new person in your team? Ask for one here. Fill the form, and it goes for approval.',
  jobs: 'All our open jobs, and who applied to each one.',
  offers: 'Job offers we send to the people we want to hire.',
  approvals: 'Things waiting for a yes or a no.',
  progress: 'See how each job is doing: how many applied, how many are in interviews, and who got an offer.',
  jobboard: 'Where our jobs are posted for people to see.',
  pipeline: 'Every candidate’s journey, step by step, from “applied” to “hired”.',
  interviews: 'All interviews: who, when, and how it went.',
  assessments: 'Tests candidates take.',
  calendar: 'Your interviews and meetings on one calendar. Nothing gets missed.',
  analytics: 'Charts that show how hiring is going.',
  reports: 'Ready-made reports you can download.',
  recruiterhub: 'How each recruiter is doing.',
  managers: 'The recruiters who find people for your jobs. See who is working on what.',
  departments: 'The teams in our company.',
  deptmanagers: 'Who looks after each department.',
  settings: 'Change how TalentFlow works.',
  activity: 'Who uses the tool, and what it costs.',
  help: 'Stuck? Find simple answers here, any time.',
}

/** What is on each page and what to do there: a few short points under the sentence above.
    Written from the pages as they are (screens/*.jsx); a point that needs a tag the user
    lacks is left out (`can`). Pages without an entry show just the sentence. */
const PAGE_POINTS = {
  import: (can) => [
    'To check one CV against a job: pick the job in “Score against”, then drop the PDF. You get an ATS score (how well the person fits).',
    'To save many CVs for later: pick “No job — store in CV bank” and drop them all. They go to the CV Bank.',
    can('cv_import.view') && '“My uploads” at the bottom lists every CV you sent.',
  ],
  candidates: (can) => [
    'Each row is one person who applied. Click a row to open their full profile.',
    'The ATS score shows the fit: green is strong, yellow is maybe, grey is weak.',
    'Use the search box, “All Jobs” and “Filters” to find someone fast.',
    can('candidates.create') && '“Add Candidate” adds a person by hand, with their CV.',
  ],
  cvbank: () => [
    'Find someone by searching their name, skill or company.',
    'To check someone for a job: click “Pick a job”, then “Run ATS” to get a score.',
    'Good fit? Click “Assign” and they move to that job.',
  ],
  requisitions: (can) => [
    can('requisitions.create') && 'Click “New Requisition” and fill the form: team, job title, grade and job description.',
    '“Save form” keeps it as a Draft. Then press the send button on its row to send it for approval.',
    'The Approval column shows where it is: Draft, In approval, Approved or Rejected.',
  ],
  progress: () => [
    'Pick a job on the left to see its numbers.',
    'The funnel shows how many people reached each step: shortlisted, interviewed, offered, hired.',
    '“Attention needed” shows what to follow up on, like people on hold or a job open too long.',
  ],
  calendar: () => [
    'Each day shows the interviews planned for it.',
    'Use the arrows to change the month, and “Today” to come back.',
    'Click an interview to open that candidate.',
  ],
  managers: () => [
    'Each card is a recruiter who helps fill your jobs.',
    'It shows how many open requests they are working on.',
    'Click “View” on a card to see more about them.',
  ],
  help: () => [
    'Email our team using the addresses shown. They will help you.',
    'You can take this tour again any time: click your name at the top, then “Take the tour”.',
  ],
}

/** Access Control actions a page can grant, in plain words, for the "You can" chips. */
const ACTIONS = [
  ['create', 'Add new'],
  ['edit', 'Change'],
  ['delete', 'Remove'],
  ['export', 'Download'],
  ['approve', 'Approve'],
]

/** "You can: Add new · Change" from the page's module tags (jobs.view → jobs.create, …). */
function abilities(route, can) {
  const perms = Array.isArray(route.permission) ? route.permission : [route.permission]
  const modules = perms.filter(Boolean).map((p) => p.split('.')[0])
  if (!modules.length) return []
  return ACTIONS.filter(([action]) => modules.some((m) => can(`${m}.${action}`))).map(([, label]) => label)
}

/* ---------------- steps ---------------- */

/**
 * A step: { key, target?, icon, tone, eyebrow, title, body, points?, chips?, hero? }.
 * `target` is a CSS selector for the thing it points at; when that is not on screen
 * (a phone, a collapsed menu) the step shows as a card without the spotlight.
 * `optional`: drop the step when its target is not on screen (e.g. a desktop-only button).
 */
function appTour({ user, can }) {
  const first = String(user?.name || '').trim().split(/\s+/)[0] || 'there'
  const pages = ROUTES.filter((r) => !r.hidden && can(r.permission))
  const ready = pages.filter((r) => !r.comingSoon)
  const soon = pages.filter((r) => r.comingSoon)
  const tones = ['var(--primary)', 'var(--info)', 'var(--purple)', 'var(--warning)', 'var(--success)', 'var(--danger)']

  const steps = [{
    key: 'welcome',
    hero: true,
    icon: 'sparkles',
    tone: 'var(--primary)',
    eyebrow: 'Welcome',
    title: `Hi ${first}! Welcome to TalentFlow`,
    body: 'This is where we find and hire great people. Let us show you around. It takes about a minute.',
  }]

  let n = 0
  for (const group of NAV_GROUPS) {
    for (const r of ready.filter((p) => p.group === group)) {
      steps.push({
        key: `page-${r.path}`,
        target: `.sidebar a.nav-item[href="/${r.path}"]`,
        icon: r.icon,
        tone: tones[n++ % tones.length],
        eyebrow: group === 'Overview' ? 'Your pages' : group,
        title: r.title,
        body: PAGE_COPY[r.path] || `Open ${r.title} from the menu on the left.`,
        points: (PAGE_POINTS[r.path]?.(can) || []).filter(Boolean),
        chips: abilities(r, can),
      })
    }
  }
  if (soon.length) {
    steps.push({
      key: 'coming-soon',
      target: `.sidebar a.nav-item[href="/${soon[0].path}"]`,
      icon: 'zap',
      tone: 'var(--warning)',
      eyebrow: 'Coming soon',
      title: soon.map((r) => r.title).join(' and '),
      body: 'Smart helpers that will save you time. They are on the way. Nothing to do here yet.',
    })
  }

  steps.push(
    {
      key: 'menu',
      target: '.topbar .menu-toggle',
      optional: true,
      icon: 'grid',
      tone: 'var(--primary)',
      eyebrow: 'On your phone',
      title: 'The menu',
      body: 'Tap here to open the menu. Every page you just saw lives in it.',
    },
    {
      key: 'search',
      target: '.topbar .topbar-search, .topbar .tb-search-btn',
      optional: true,
      icon: 'search',
      tone: 'var(--info)',
      eyebrow: 'Top bar',
      title: 'Search',
      body: 'Looking for someone or something? Type it here and we will find it.',
    },
    {
      key: 'notifications',
      target: '.topbar [aria-label="Notifications"]',
      optional: true,
      icon: 'bell',
      tone: 'var(--warning)',
      eyebrow: 'Top bar',
      title: 'Notifications',
      body: 'The bell lights up when something new happens for you.',
    },
    {
      key: 'theme',
      target: '.topbar .tb-theme',
      optional: true,
      icon: 'moon',
      tone: 'var(--purple)',
      eyebrow: 'Top bar',
      title: 'Light or dark',
      body: 'Pick the look that is easy on your eyes. Tap to switch.',
    },
    {
      key: 'profile',
      target: '.topbar .profile-btn',
      optional: true,
      icon: 'user',
      tone: 'var(--success)',
      eyebrow: 'Top bar',
      title: 'You',
      body: 'Your name lives here. Open it for your profile, help, or to log out.',
    },
    {
      key: 'ai',
      target: '.ai-fab',
      optional: true,
      icon: 'sparkles',
      tone: 'var(--primary)',
      eyebrow: 'Helper',
      title: 'AI assistant',
      body: 'Have a question? Ask it here in plain words.',
    },
    {
      key: 'done',
      hero: true,
      finale: true,
      icon: 'check-circle',
      tone: 'var(--success)',
      eyebrow: 'All done',
      title: 'You’re all set!',
      body: pages.some((r) => r.path === 'help')
        ? 'That’s the whole tour. If you ever get stuck, open Help from the menu.'
        : 'That’s the whole tour. Have fun!',
    },
  )
  return steps
}
